import React, { useCallback, useEffect, useState } from 'react'

import { ButtonContainer, PageTitle, SinglePanePage } from 'styles/shared'
import styled from 'styled-components'
import { color } from 'styles/colors'
import ArrowIcon from '@mui/icons-material/TrendingFlat'

import Modal from 'components/Modal'
import ModalButton from 'components/Modal/Button'
import Spinner from 'components/Spinner'

import GA4 from 'helpers/ga4'
import { shortDate } from 'helpers/formatters'

import Api from 'services/api'

import { PaywallPlanChange } from 'types/PaywallPlanChange'

const NO_PLAN_CHANGE = 'No more changes can be made at this time.'
const DOWNGRADE_MESSAGE = 'If you\'d like to change to the Monthly plan, please contact support \n844-359-0270.'
const CHANGE_TO_YEARLY = 'On the next billing cycle, your plan will be updated to yearly and you will be charged USD $2400.'

type Props = {
  closeCallback: (reload: boolean) => void
  dealerId: number
  currentPlan: number
  planChange?: PaywallPlanChange
  yearlyPlanNo: number
}

const ChangePlanModal = ({ dealerId, planChange, currentPlan, closeCallback, yearlyPlanNo } : Props) => {
  const [isSaving, setIsSaving] = useState(false)
  const [allowPlanChange, setAllowPlanChange] = useState(false)
  const [planIsAlreadyChanging, setPlanIsAlreadyChanging] = useState(false)

  useEffect(() => {
    if (planChange?.effectiveDate) {
      setPlanIsAlreadyChanging(true)
    }
    if (currentPlan === 0) {
      setAllowPlanChange(false)
    } else if (currentPlan !== yearlyPlanNo && !planChange?.effectiveDate) {
      setAllowPlanChange(true)
    }
  }, [planChange, currentPlan, yearlyPlanNo])

  const changeInProgress = `A change to the plan has already been initiated and will take effect on ${shortDate(planChange?.effectiveDate)}.`

  const onClickChangeToYearly = useCallback(async () => {
    GA4.sendEvent({
      action: 'Click Change to Yearly',
      category: 'Premium Settings'
    })

    setIsSaving(true)
    await Api.Paywall.updatePlan(dealerId, yearlyPlanNo)
      .then(() => {
        closeCallback(true)
      }).catch(() => setIsSaving(false))
  }, [closeCallback, dealerId, yearlyPlanNo])

  const onClickClose = () => {
    closeCallback(false)
  }

  return (
    <Modal variant="sm" isDisplayed closeCallback={() => closeCallback(false)} title="">
      <S.Top />
      <SinglePanePage>
        <S.PageTitle>Change Plan</S.PageTitle>
        <S.Form>
          {planIsAlreadyChanging &&
            <>
              <S.Explanation>{NO_PLAN_CHANGE}</S.Explanation>
              <S.Explanation>{changeInProgress}</S.Explanation>
            </>
          }
          {!allowPlanChange && !planIsAlreadyChanging &&
            <>
              <S.Explanation>{DOWNGRADE_MESSAGE}</S.Explanation>
            </>
          }
        </S.Form>
        {allowPlanChange &&
          <>
            <S.Explanation>{CHANGE_TO_YEARLY}</S.Explanation>
            <S.ButtonContainer>
              <ModalButton decoration="secondary"
                           text="Close"
                            onPress={onClickClose}
                            styles={{ button: { height: 32, borderRadius: 10, width: 120 } }}
              />
              <ModalButton decoration="positive"
                      text="Change to Yearly"
                      onPress={onClickChangeToYearly}
                      showSpinner={isSaving}
                      styles={{ button: { height: 32, borderRadius: 10, width: 120 } }}
              />
            </S.ButtonContainer>
          </>}
        {!allowPlanChange &&
        <S.ButtonContainer>
          <ModalButton decoration="positive"
                      text="Close"
                      onPress={onClickClose}
                      styles={{ button: { height: 32, borderRadius: 10, width: 120 } }}
          />
        </S.ButtonContainer>
          }
      </SinglePanePage>
    </Modal>
  )
}

export default ChangePlanModal

const S = {
  ArrowIcon: styled(ArrowIcon)`
    transform: rotate(180deg);
    color: ${color('breadCrumbsTextHover')};
  `,
  ButtonContainer: styled(ButtonContainer)`
    border-top: none;
    max-width: 800px;
    margin-top: 0px;
  `,
  Explanation: styled.div`
    display: flex;
    margin-bottom: 20px;
    text-align: left;
    color: ${color('premiumText')};
    white-space: pre-line;
  `,
  PageTitle: styled(PageTitle)`
    margin-top: 10px;
    text-transform: none;
  `,
  InnerContainer: styled.div`
    display: flex;
    flex-direction: column;
    background-color: #F3F7F8;
    padding: 10px 15px 15px 15px;
  `,
  Form: styled.form`
    display: flex;
    grid-column-gap: 50px;
    flex-wrap: wrap;
    justify-content: space-between;
  `,
  Spinner: styled(Spinner)`
    margin: 60px 0 0 0;
    width: 32px;
    height: 32px;
    border-radius: 18px;
  `,
  Top: styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 10px;
  `
}
